import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BeakerIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import {
  listResearchAgents,
  getResearchBrief,
  listKnowledgeItems,
} from "../../api/adminApi";

const Card = ({ children, className = "" }) => (
  <div className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}>
    {children}
  </div>
);

const Stat = ({ label, value, hint }) => (
  <div>
    <p className="text-xs uppercase tracking-widest text-gray-500">{label}</p>
    <p className="text-3xl font-bold mt-1">{value}</p>
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </div>
);

const STATUS_STYLES = {
  success: "bg-emerald-500/15 text-emerald-500",
  error: "bg-red-500/15 text-red-500",
  running: "bg-sky-500/15 text-sky-500",
  never: "bg-gray-500/15 text-gray-400",
};

const PIE_COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ec4899", "#06b6d4", "#a855f7", "#ef4444", "#84cc16"];

const fmtDate = (iso) => {
  if (!iso) return "never";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

const AgentCard = ({ a }) => {
  const last = a.last_run;
  const statusKey = last ? last.status : "never";
  return (
    <Link
      to={`/admin/research/${a.key}`}
      className="group p-5 rounded-2xl border border-gray-200 dark:border-white/10 bg-white/40 dark:bg-white/[0.02] hover:border-indigo-400 dark:hover:border-indigo-500/60 hover:bg-white dark:hover:bg-white/[0.05] transition-colors flex flex-col"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-bold leading-tight">{a.name}</h3>
        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_STYLES[statusKey] || STATUS_STYLES.never}`}>
          {last ? last.status : "no runs"}
        </span>
      </div>
      <p className="text-xs text-gray-500 mt-0.5 capitalize">{a.domain} · {(a.jurisdictions || []).join(" / ")}</p>

      <div className="grid grid-cols-3 gap-2 mt-4 text-center">
        <div>
          <p className="text-xl font-bold">{a.active_items}</p>
          <p className="text-[10px] uppercase tracking-wide text-gray-500">active</p>
        </div>
        <div>
          <p className={`text-xl font-bold ${a.pending_items ? "text-amber-500" : ""}`}>{a.pending_items}</p>
          <p className="text-[10px] uppercase tracking-wide text-gray-500">pending</p>
        </div>
        <div>
          <p className="text-xl font-bold">{a.source_count}</p>
          <p className="text-[10px] uppercase tracking-wide text-gray-500">sources</p>
        </div>
      </div>

      <p className="text-[11px] text-gray-500 mt-3">
        Feeds: {(a.feeds_specialists || []).join(", ") || "—"} · Last run {fmtDate(a.last_run_at)}
      </p>
    </Link>
  );
};

const ResearchOverview = () => {
  const [agents, setAgents] = useState([]);
  const [brief, setBrief] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, b, it] = await Promise.all([
          listResearchAgents(),
          getResearchBrief().catch(() => null),
          listKnowledgeItems().catch(() => []),
        ]);
        setAgents(a || []);
        setBrief(b);
        setItems(it || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalActive = agents.reduce((s, a) => s + (a.active_items || 0), 0);
  const totalPending = agents.reduce((s, a) => s + (a.pending_items || 0), 0);
  const totalSources = agents.reduce((s, a) => s + (a.source_count || 0), 0);

  // Charts data
  const itemsByDesk = agents
    .map((a) => ({ name: a.key, active: a.active_items, pending: a.pending_items }))
    .filter((d) => d.active || d.pending);

  const jurisCounts = items.reduce((acc, it) => {
    acc[it.jurisdiction] = (acc[it.jurisdiction] || 0) + 1;
    return acc;
  }, {});
  const jurisData = Object.entries(jurisCounts).map(([name, value]) => ({ name, value }));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Research Desk</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-2xl">
            World-class researcher agents that keep the Council current. They propose
            knowledge for your approval, and approved items feed the specialists
            (Compass, Lens, Vigil, Tally, Terra).
          </p>
        </div>
        <Link
          to="/admin/research/approvals"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold"
        >
          <ClipboardDocumentCheckIcon className="w-5 h-5" />
          Review queue
          {totalPending > 0 && (
            <span className="ml-1 px-2 py-0.5 rounded-full bg-white/20 text-xs">{totalPending}</span>
          )}
        </Link>
      </div>

      {/* Top stats */}
      <Card>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
          <Stat label="Desks" value={loading ? "…" : agents.length} />
          <Stat label="Active knowledge" value={loading ? "…" : totalActive} />
          <Stat label="Pending review" value={loading ? "…" : totalPending} hint="awaiting your approval" />
          <Stat label="Sources referenced" value={loading ? "…" : totalSources} />
        </div>
      </Card>

      {/* Board-meeting brief */}
      {brief && (
        <Card>
          <div className="flex items-center gap-2 mb-3">
            <BeakerIcon className="w-5 h-5 text-indigo-500" />
            <h2 className="text-lg font-bold">Board brief</h2>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-200 mb-4">{brief.headline}</p>
          {(brief.desks || []).length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {brief.desks.map((d) => (
                <div key={d.key} className="p-3 rounded-xl border border-gray-200 dark:border-white/10">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-sm">{d.name}</p>
                    <span className="text-[11px] text-gray-500">
                      {d.pending ? `${d.pending} to review` : ""}{d.pending && d.applied ? " · " : ""}{d.applied ? `${d.applied} applied` : ""}
                    </span>
                  </div>
                  <ul className="mt-1.5 space-y-0.5">
                    {(d.bullets || []).map((b, i) => (
                      <li key={i} className="text-xs text-gray-600 dark:text-gray-400 truncate">• {b}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">All quiet — run a desk to populate the brief.</p>
          )}
        </Card>
      )}

      {/* Charts */}
      {!loading && (itemsByDesk.length > 0 || jurisData.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">Knowledge items by desk</p>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={itemsByDesk}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip />
                  <Bar dataKey="active" stackId="a" fill="#6366f1" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="pending" stackId="a" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">Items by jurisdiction</p>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={jurisData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {jurisData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}

      {/* Desk scorecards */}
      <div>
        <h2 className="text-lg font-bold mb-3">Desks</h2>
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {agents.map((a) => (
              <AgentCard key={a.key} a={a} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResearchOverview;
